<template>
  <v-container fluid class="forum-admin">
    <p class="text-center title mt-6">Форум</p>

    <v-row class="mt-3">
      <!-- topics -->
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4">
          <p class="font-weight-bold mb-3">Теми</p>
          <div v-for="item in categories" :key="item.id" class="forum-admin__topic">
            <template v-if="editingId === item.id">
              <v-text-field v-model="form.name" label="Назва" dense outlined hide-details class="mb-2"></v-text-field>
              <v-text-field v-model="form.slug" label="Адреса (латиницею)" :prefix="'/forum/'" dense outlined hide-details class="mb-2"></v-text-field>
              <v-textarea v-model="form.description" label="Опис для сторінки теми" dense outlined hide-details rows="2" auto-grow class="mb-2"></v-textarea>
              <v-text-field v-model.number="form.sort" label="Порядок" type="number" dense outlined hide-details class="mb-2"></v-text-field>
              <div class="d-flex">
                <v-btn small color="primary" depressed :loading="busy" @click="saveCategory">Зберегти</v-btn>
                <v-btn small text @click="editingId = null">Скасувати</v-btn>
              </div>
            </template>
            <div v-else class="d-flex align-center">
              <div class="flex-grow-1">
                <div>{{ item.name }}</div>
                <div class="caption grey--text">/forum/{{ item.slug }} · питань: {{ item.questions_count }}</div>
              </div>
              <v-btn icon small @click="editCategory(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
              <v-btn icon small color="red" @click="deleteCategory(item)"><v-icon small>mdi-delete</v-icon></v-btn>
            </div>
          </div>
          <template v-if="editingId === 'new'">
            <v-text-field v-model="form.name" label="Назва нової теми" dense outlined hide-details class="mb-2 mt-3"></v-text-field>
            <v-textarea v-model="form.description" label="Опис (необов'язково)" dense outlined hide-details rows="2" auto-grow class="mb-2"></v-textarea>
            <div class="d-flex">
              <v-btn small color="primary" depressed :loading="busy" @click="saveCategory">Додати</v-btn>
              <v-btn small text @click="editingId = null">Скасувати</v-btn>
            </div>
          </template>
          <v-btn v-else small class="mt-3" @click="newCategory">Додати тему <v-icon small color="green">mdi-plus</v-icon></v-btn>
        </v-card>
      </v-col>

      <!-- moderation -->
      <v-col cols="12" md="8">
        <v-tabs v-model="statusTab" background-color="primary" dark>
          <v-tab v-for="tab in statusTabs" :key="tab.value">
            {{ tab.label }}<span v-if="tab.value === 'pending' && pending" class="ml-1">({{ pending }})</span>
          </v-tab>
        </v-tabs>
        <v-text-field
          v-model="searchInput"
          label="Пошук за текстом або автором"
          prepend-inner-icon="mdi-magnify"
          dense
          outlined
          hide-details
          clearable
          class="mt-3"
        ></v-text-field>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <p v-else-if="!questions.length" class="text-center grey--text py-8">Питань немає</p>

        <v-card v-for="item in questions" :key="item.id" outlined class="pa-4 mt-3">
          <div class="d-flex flex-wrap align-center forum-admin__meta">
            <v-chip x-small :color="statusColor(item.status)" text-color="white">{{ statusLabel(item.status) }}</v-chip>
            <span>{{ item.category || "без теми" }}</span>
            <span>· {{ item.author_name }}<template v-if="item.author"> ({{ item.author.email || item.author.phone }})</template></span>
            <span>· {{ dateOf(item.created_at) }}</span>
            <a v-if="item.status === 'published'" :href="siteForum + '/q/' + item.path" target="_blank" rel="noopener" class="ml-auto">на сайті</a>
          </div>
          <p class="font-weight-bold mt-2 mb-1">{{ item.title }}</p>
          <p class="forum-admin__text">{{ item.body }}</p>
          <div class="d-flex flex-wrap forum-admin__actions">
            <v-btn v-if="item.status !== 'published'" x-small color="green" dark @click="setStatus(item, 'published')">Опублікувати</v-btn>
            <v-btn v-if="item.status !== 'hidden'" x-small outlined @click="setStatus(item, 'hidden')">Приховати</v-btn>
            <v-btn x-small text color="red" @click="deleteQuestion(item)">Видалити</v-btn>
          </div>

          <div v-for="answer in item.answers" :key="answer.id" class="forum-admin__answer">
            <div class="caption grey--text">
              {{ answer.psychologist }} · {{ dateOf(answer.created_at) }}
              <span v-if="answer.status === 'hidden'" class="red--text"> · приховано</span>
            </div>
            <p class="forum-admin__text mb-1">{{ answer.body }}</p>
            <v-btn x-small outlined @click="setAnswerStatus(answer, answer.status === 'hidden' ? 'published' : 'hidden')">
              {{ answer.status === "hidden" ? "Показати" : "Приховати" }}
            </v-btn>
            <v-btn x-small text color="red" @click="deleteAnswer(item, answer)">Видалити</v-btn>
            <span class="forum-admin__likes">
              Лайки
              <input v-model="answer.likes_count" type="number" min="0" class="forum-admin__likes-input" @keyup.enter="saveAnswerLikes(answer)" />
              <v-btn x-small outlined @click="saveAnswerLikes(answer)">Зберегти</v-btn>
            </span>

            <!-- the dialog under the answer: the question's author and the psychologist -->
            <div v-for="reply in answer.replies" :key="reply.id" class="forum-admin__reply">
              <div class="caption grey--text">
                {{ reply.author }} ({{ reply.from === "author" ? "автор питання" : "психолог" }}) · {{ dateOf(reply.created_at) }}
                <span v-if="reply.status === 'hidden'" class="red--text"> · приховано</span>
              </div>
              <p class="forum-admin__text mb-1">{{ reply.body }}</p>
              <v-btn x-small outlined @click="setReplyStatus(reply, reply.status === 'hidden' ? 'published' : 'hidden')">
                {{ reply.status === "hidden" ? "Показати" : "Приховати" }}
              </v-btn>
              <v-btn x-small text color="red" @click="deleteReply(answer, reply)">Видалити</v-btn>
            </div>
          </div>
        </v-card>

        <v-pagination v-if="lastPage > 1" v-model="page" :length="lastPage" :total-visible="7" class="mt-4"></v-pagination>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

const STATUS = {
  published: { label: "Опубліковано", color: "green" },
  pending: { label: "На перевірці", color: "orange" },
  hidden: { label: "Приховано", color: "grey" },
};

export default {
  name: "ForumAdmin",
  metaInfo() {
    return { title: "Платформа / Форум" };
  },
  data() {
    return {
      categories: [],
      editingId: null,
      form: {},
      busy: false,
      statusTabs: [
        { value: "", label: "Усі" },
        { value: "pending", label: "На перевірці" },
        { value: "published", label: "Опубліковані" },
        { value: "hidden", label: "Приховані" },
      ],
      statusTab: 0,
      searchInput: "",
      search: "",
      page: 1,
      lastPage: 1,
      pending: 0,
      questions: [],
      loading: false,
      siteForum: process.env.VUE_APP_MAIN_PAGE + "/forum",
    };
  },
  watch: {
    statusTab() {
      this.reload();
    },
    page() {
      this.loadQuestions();
    },
    searchInput(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.search = (value || "").trim();
        this.reload();
      }, 400);
    },
  },
  mounted() {
    this.loadCategories();
    this.loadQuestions();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    statusLabel(status) {
      return (STATUS[status] || {}).label || status;
    },
    statusColor(status) {
      return (STATUS[status] || {}).color || "grey";
    },
    dateOf(value) {
      return new Date(value).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    toast(text) {
      Vue.swal({ target: document.getElementById("main"), text, icon: "success", timer: 2500, toast: true, position: "top-right", showConfirmButton: false });
    },
    loadCategories() {
      return ApiClient.get("api/admin/forum/categories").then((response) => {
        this.categories = response.data.data;
      });
    },
    newCategory() {
      this.form = { name: "", description: "" };
      this.editingId = "new";
    },
    editCategory(item) {
      this.form = { name: item.name, slug: item.slug, description: item.description, sort: item.sort };
      this.editingId = item.id;
    },
    saveCategory() {
      this.busy = true;
      const url = this.editingId === "new" ? "api/admin/forum/categories" : `api/admin/forum/categories/${this.editingId}`;
      ApiClient.post(url, this.form)
        .then((response) => {
          if (!response) return;
          this.editingId = null;
          this.toast(response.data.message);
          this.loadCategories();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    deleteCategory(item) {
      if (!window.confirm(`Видалити тему «${item.name}»? Питання залишаться без теми.`)) return;
      ApiClient.delete(`api/admin/forum/categories/${item.id}`).then((response) => {
        this.toast(response.data.message);
        this.loadCategories();
      });
    },
    reload() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.loadQuestions();
      }
    },
    loadQuestions() {
      const params = new URLSearchParams({ page: this.page });
      const status = this.statusTabs[this.statusTab].value;
      if (status) params.append("status", status);
      if (this.search) params.append("search", this.search);
      this.loading = true;
      return ApiClient.get("api/admin/forum/questions?" + params.toString())
        .then((response) => {
          this.questions = response.data.data;
          this.lastPage = response.data.meta.last_page || 1;
          this.pending = response.data.meta.pending;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setStatus(item, status) {
      ApiClient.patch(`api/admin/forum/questions/${item.id}`, { status }).then((response) => {
        this.toast(response.data.message);
        this.loadQuestions();
        this.loadCategories();
      });
    },
    deleteQuestion(item) {
      if (!window.confirm(`Видалити питання «${item.title}» разом з відповідями?`)) return;
      ApiClient.delete(`api/admin/forum/questions/${item.id}`).then((response) => {
        this.toast(response.data.message);
        this.loadQuestions();
        this.loadCategories();
      });
    },
    setAnswerStatus(answer, status) {
      ApiClient.patch(`api/admin/forum/answers/${answer.id}`, { status }).then((response) => {
        answer.status = status;
        this.toast(response.data.message);
      });
    },
    saveAnswerLikes(answer) {
      const likes_count = Math.max(0, parseInt(answer.likes_count, 10) || 0);
      ApiClient.patch(`api/admin/forum/answers/${answer.id}`, { likes_count }).then((response) => {
        answer.likes_count = likes_count;
        this.toast(response.data.message);
      });
    },
    setReplyStatus(reply, status) {
      ApiClient.patch(`api/admin/forum/replies/${reply.id}`, { status }).then((response) => {
        reply.status = status;
        this.toast(response.data.message);
      });
    },
    deleteReply(answer, reply) {
      if (!window.confirm("Видалити повідомлення?")) return;
      ApiClient.delete(`api/admin/forum/replies/${reply.id}`).then((response) => {
        answer.replies = answer.replies.filter((other) => other.id !== reply.id);
        this.toast(response.data.message);
      });
    },
    deleteAnswer(item, answer) {
      if (!window.confirm("Видалити відповідь?")) return;
      ApiClient.delete(`api/admin/forum/answers/${answer.id}`).then((response) => {
        item.answers = item.answers.filter((other) => other.id !== answer.id);
        this.toast(response.data.message);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.forum-admin__topic {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}
.forum-admin__meta {
  gap: 6px;
  font-size: 13px;
  color: #868686;
}
.forum-admin__text {
  white-space: pre-line;
}
.forum-admin__actions {
  gap: 6px;
}
.forum-admin__answer {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f4f8fa;
  border-radius: 8px;
}
.forum-admin__likes {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  font-size: 13px;
  color: #868686;
}
.forum-admin__likes-input {
  width: 72px;
  padding: 2px 6px;
  border: 1px solid #d9e4eb;
  border-radius: 4px;
  background: #fff;
}
.forum-admin__reply {
  margin: 10px 0 0 16px;
  padding-left: 12px;
  border-left: 2px solid #d9e4eb;
}
</style>
